import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { Mail, ArrowLeft, Loader2, Check } from "lucide-react";
import Logo from "../components/Logo";
import { BACKEND_URL } from "../lib/config";

export default function ForgotPassword() {
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);
  const [sent, setSent] = useState(false);
  const [error, setError] = useState("");
  const navigate = useNavigate();

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      await axios.post(`${BACKEND_URL}/api/auth/forgot-password`, { email });
      setSent(true);
    } catch (err) {
      // Backend ritorna sempre 200, ma per sicurezza gestiamo network error
      setError("Errore di connessione. Riprova più tardi.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen relative flex justify-center">
      <div
        className="absolute inset-0 -z-10"
        style={{
          background:
            "radial-gradient(700px 400px at 50% -10%, rgba(20,194,161,0.35) 0%, rgba(20,194,161,0) 60%), #141818",
        }}
      />
      <div className="w-full max-w-[430px] min-h-screen bg-[#2b3134] flex flex-col">
        <div className="pt-10 pb-6 flex flex-col items-center gap-3">
          <Logo />
          <div className="text-white/60 text-[12px] uppercase tracking-[0.25em]">
            Recupero password
          </div>
        </div>
        <div className="px-6 flex-1">
          <button
            onClick={() => navigate("/login")}
            className="text-white/70 hover:text-white text-sm flex items-center gap-1.5 mb-6"
            data-testid="back-to-login"
          >
            <ArrowLeft size={14} /> Torna al login
          </button>

          {!sent ? (
            <>
              <h2 className="text-white text-[22px] font-semibold mb-2">
                Hai dimenticato la password?
              </h2>
              <p className="text-white/65 text-[13.5px] leading-relaxed mb-5">
                Inserisci la tua email: ti invieremo un link per impostare una
                nuova password. Il link sarà valido per 24 ore.
              </p>
              <form onSubmit={submit} className="space-y-3">
                <div className="flex items-center h-12 px-4 gap-3 rounded-2xl bg-white/5 border border-white/10 focus-within:border-[#14c2a1] focus-within:ring-2 focus-within:ring-[#14c2a1]/25 transition-all">
                  <Mail size={16} className="text-white/50" />
                  <input
                    type="email"
                    required
                    placeholder="La tua email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    data-testid="forgot-email-input"
                    className="flex-1 bg-transparent text-white text-sm placeholder-white/40 focus:outline-none"
                  />
                </div>
                {error && (
                  <div className="text-rose-300 text-sm bg-rose-500/10 border border-rose-400/20 rounded-lg px-3 py-2">
                    {error}
                  </div>
                )}
                <button
                  type="submit"
                  disabled={loading || !email}
                  data-testid="forgot-submit"
                  className="w-full h-12 rounded-2xl text-white font-semibold text-[15px] flex items-center justify-center gap-2 disabled:opacity-60 transition-all"
                  style={{
                    background:
                      "linear-gradient(155deg, #12b096 0%, #0a8670 100%)",
                    boxShadow: "0 14px 30px -12px rgba(20,194,161,0.55)",
                  }}
                >
                  {loading && <Loader2 size={16} className="animate-spin" />}
                  Invia link di reset
                </button>
              </form>
            </>
          ) : (
            <div
              className="rounded-2xl bg-[#14c2a1]/12 border border-[#14c2a1]/30 p-5 text-center"
              data-testid="forgot-sent-state"
            >
              <div className="w-12 h-12 rounded-full bg-[#14c2a1]/25 flex items-center justify-center mx-auto">
                <Check size={22} className="text-[#67e8d1]" strokeWidth={3} />
              </div>
              <h3 className="mt-3 text-white text-[18px] font-semibold">
                Email inviata
              </h3>
              <p className="mt-2 text-white/75 text-[13.5px] leading-relaxed">
                Se l'indirizzo <strong className="text-white">{email}</strong> è
                registrato, riceverai a breve un'email con il link per
                reimpostare la password.
              </p>
              <p className="mt-3 text-white/55 text-[12px]">
                Controlla anche la cartella spam. Il link è valido per 24 ore.
              </p>
              <button
                onClick={() => navigate("/login")}
                className="mt-5 w-full h-11 rounded-full border border-white/30 text-white text-sm hover:bg-white/5 transition-colors"
              >
                Torna al login
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
